// Distance, and how honest we are about it.
//
// Two separate ideas live here and both matter. The first is arithmetic:
// straight-line distance between two points. The second is a judgement — when
// is a fix too rough to state a distance plainly? A phone that hands us a
// 2 km accuracy radius (wifi triangulation, a VPN, a manual postal code) can
// reorder two branches that are three streets apart. The number is still
// useful; presenting it as exact is not.
//
// NOT a mock. This is the same arithmetic against real coordinates.

const EARTH_RADIUS_M = 6371008.8;

const toRadians = (deg) => (deg * Math.PI) / 180;

// Straight-line ("as the crow flies"), NOT travel distance. Nothing in this
// stage routes, so nothing here should pretend to. Every label this module
// produces is honest about that by staying a plain distance, and the UI says
// "away" rather than "drive".
export function distanceMeters(from, to) {
  if (!from || !to) return null;
  const dLat = toRadians(to.lat - from.lat);
  const dLon = toRadians(to.lon - from.lon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

// Above this, two nearby branches can swap places purely from fix error, so
// every distance derived from that fix gets marked approximate. 500 m is about
// the point where a city-block ordering stops being trustworthy.
export const APPROXIMATE_ACCURACY_M = 500;

// A typed-in city or postal code resolves to a centroid, not to where the user
// is standing. That is approximate by construction, whatever accuracy figure
// comes back with it.
export function isApproximate(location) {
  if (!location) return false;
  if (location.source === 'manual') return true;
  const accuracy = location.accuracyMeters;
  return typeof accuracy === 'number' ? accuracy > APPROXIMATE_ACCURACY_M : true;
}

// Country-aware units, without a country catalogue: everywhere is metric
// except the handful of places that measure road distance in miles. Currency
// and address formats are deliberately out of scope for this stage — no prices
// are shown, so there is nothing to format.
const IMPERIAL_COUNTRIES = new Set(['US', 'GB', 'LR', 'MM']);

export function measurementSystemFor(countryCode) {
  return IMPERIAL_COUNTRIES.has(String(countryCode || '').toUpperCase()) ? 'imperial' : 'metric';
}

export function formatDistance(meters, { system = 'metric', approximate = false } = {}) {
  if (typeof meters !== 'number' || !Number.isFinite(meters) || meters < 0) return null;
  const prefix = approximate ? '~' : '';

  if (system === 'imperial') {
    const miles = meters / 1609.344;
    if (miles < 0.1) return `${prefix}under 0.1 mi`;
    return `${prefix}${miles.toFixed(1)} mi`;
  }

  // Sub-kilometre in metres, rounded to 50 — a rounder number is a more honest
  // one at this resolution, and it reads better on a shop row.
  if (meters < 1000) {
    const rounded = Math.max(50, Math.round(meters / 50) * 50);
    return `${prefix}${rounded} m`;
  }
  return `${prefix}${(meters / 1000).toFixed(1)} km`;
}
